import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Loader2, ArrowLeft, Save, Palette, Settings as SettingsIcon, Users, Trash2, Plus, ShieldAlert } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { BrandingPreview } from "@/components/admin/BrandingPreview";
import { AssetUploadField } from "@/components/admin/AssetUploadField";
import { searchUsersForMembership, type TenantBrandingRow, type TenantSettingsRow, type MembershipRow } from "@/lib/admin-tenants";

export const Route = createFileRoute("/admin/tenants/$tenantId")({
  head: () => ({ meta: [{ title: "Admin · Editar tenant — ZYNTRA" }] }),
  component: TenantEditor,
});

type Tab = "branding" | "settings" | "members";

function TenantEditor() {
  const { tenantId } = Route.useParams();
  const { user, loading: authLoading } = useAuth();
  const [tab, setTab] = useState<Tab>("branding");
  const [tenant, setTenant] = useState<{ name: string; slug: string; status: string; is_default: boolean } | null>(null);
  const [branding, setBranding] = useState<TenantBrandingRow | null>(null);
  const [settings, setSettings] = useState<TenantSettingsRow | null>(null);
  const [members, setMembers] = useState<MembershipRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: "ok" | "err"; text: string } | null>(null);

  useEffect(() => {
    if (authLoading || !user) return;
    (async () => {
      setLoading(true);
      // Check access via list_accessible_tenants (RLS-safe)
      const { data: accessible } = await supabase.rpc("list_accessible_tenants");
      const ok = (accessible ?? []).some((t: any) => t.id === tenantId);
      setAllowed(ok);
      if (!ok) {
        setLoading(false);
        return;
      }

      const [{ data: t }, { data: b }, { data: s }] = await Promise.all([
        supabase.from("tenants").select("name, slug, status, is_default").eq("id", tenantId).single(),
        supabase.from("tenant_branding").select("*").eq("tenant_id", tenantId).maybeSingle(),
        supabase.from("tenant_settings").select("*").eq("tenant_id", tenantId).maybeSingle(),
      ]);
      setTenant(t);
      // Ensure rows exist (legacy tenants without branding/settings)
      let brandingRow = b as TenantBrandingRow | null;
      if (!brandingRow && t) {
        const { data: created } = await supabase
          .from("tenant_branding")
          .insert({ tenant_id: tenantId, brand_name: t.name })
          .select("*")
          .single();
        brandingRow = created as TenantBrandingRow;
      }
      let settingsRow = s as TenantSettingsRow | null;
      if (!settingsRow) {
        const { data: created } = await supabase
          .from("tenant_settings")
          .insert({ tenant_id: tenantId })
          .select("*")
          .single();
        settingsRow = created as TenantSettingsRow;
      }
      setBranding(brandingRow);
      setSettings(settingsRow);
      await loadMembers();
      setLoading(false);
    })();
  }, [tenantId, authLoading, user]);

  async function loadMembers() {
    const { data: rows } = await supabase
      .from("tenant_memberships")
      .select("id, user_id, tenant_id, role, is_primary")
      .eq("tenant_id", tenantId);
    if (!rows || rows.length === 0) {
      setMembers([]);
      return;
    }
    const userIds = rows.map((r) => r.user_id);
    const { data: profiles } = await supabase
      .from("profiles_public")
      .select("user_id, display_name")
      .in("user_id", userIds);
    const profileMap = new Map((profiles ?? []).map((p) => [p.user_id, p.display_name]));
    setMembers(
      rows.map((r) => ({
        ...r,
        display_name: profileMap.get(r.user_id) ?? null,
        email: null,
      })) as MembershipRow[]
    );
  }

  async function saveBranding() {
    if (!branding) return;
    setSaving(true);
    setMsg(null);
    const { error } = await supabase
      .from("tenant_branding")
      .update({
        brand_name: branding.brand_name,
        brand_tagline: branding.brand_tagline,
        logo_url: branding.logo_url,
        logo_dark_url: branding.logo_dark_url,
        favicon_url: branding.favicon_url,
        hero_image_url: branding.hero_image_url,
        primary_color: branding.primary_color,
        secondary_color: branding.secondary_color,
        accent_color: branding.accent_color,
        font_family_heading: branding.font_family_heading,
        font_family_body: branding.font_family_body,
        border_radius: branding.border_radius,
        show_powered_by_zyntra: branding.show_powered_by_zyntra,
      })
      .eq("id", branding.id);
    setSaving(false);
    if (error) setMsg({ type: "err", text: error.message });
    else setMsg({ type: "ok", text: "Branding guardado." });
  }

  async function saveSettings() {
    if (!settings) return;
    setSaving(true);
    setMsg(null);
    const { error } = await supabase
      .from("tenant_settings")
      .update({
        locale: settings.locale,
        currency: settings.currency,
        timezone: settings.timezone,
        support_email: settings.support_email,
        contact_phone: settings.contact_phone,
        terms_url: settings.terms_url,
        privacy_url: settings.privacy_url,
        checkout_message: settings.checkout_message,
        tickets_footer_text: settings.tickets_footer_text,
        emails_enabled: settings.emails_enabled,
        resale_enabled_default: settings.resale_enabled_default,
      })
      .eq("id", settings.id);
    setSaving(false);
    if (error) setMsg({ type: "err", text: error.message });
    else setMsg({ type: "ok", text: "Configuración guardada." });
  }

  if (authLoading || loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!user || allowed === false) {
    return (
      <div className="mx-auto max-w-md px-4 pt-32 text-center">
        <ShieldAlert className="mx-auto h-10 w-10 text-muted-foreground" />
        <h1 className="mt-3 text-xl font-bold text-foreground">Acceso restringido</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          No tenés permisos para administrar este tenant.
        </p>
        <Link to="/admin/tenants" className="mt-4 inline-block text-sm text-primary hover:underline">
          Volver a tenants
        </Link>
      </div>
    );
  }

  if (!tenant || !branding || !settings) return null;

  return (
    <div className="mx-auto max-w-6xl px-4 pb-20 pt-24 sm:px-6">
      <Link
        to="/admin/tenants"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Volver a tenants
      </Link>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{tenant.name}</h1>
          <p className="text-sm text-muted-foreground">
            /{tenant.slug} · {tenant.status}{tenant.is_default ? " · default" : ""}
          </p>
        </div>
      </div>

      <div className="mt-6 flex gap-1 rounded-lg bg-secondary p-1">
        <TabBtn active={tab === "branding"} onClick={() => setTab("branding")} icon={<Palette className="h-4 w-4" />} label="Branding" />
        <TabBtn active={tab === "settings"} onClick={() => setTab("settings")} icon={<SettingsIcon className="h-4 w-4" />} label="Configuración" />
        <TabBtn active={tab === "members"} onClick={() => setTab("members")} icon={<Users className="h-4 w-4" />} label="Miembros" />
      </div>

      {msg && (
        <p
          className={`mt-4 rounded-lg px-3 py-2 text-xs ${
            msg.type === "ok" ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive"
          }`}
        >
          {msg.text}
        </p>
      )}

      {tab === "branding" && (
        <BrandingTab
          tenantId={tenantId}
          branding={branding}
          setBranding={setBranding}
          onSave={saveBranding}
          saving={saving}
        />
      )}
      {tab === "settings" && (
        <SettingsTab settings={settings} setSettings={setSettings} onSave={saveSettings} saving={saving} />
      )}
      {tab === "members" && (
        <MembersTab tenantId={tenantId} members={members} onChange={loadMembers} />
      )}
    </div>
  );
}

function TabBtn({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: React.ReactNode; label: string }) {
  return (
    <button
      onClick={onClick}
      className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-2 text-xs font-semibold transition-colors ${
        active ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

/* ------------------------- Branding tab ------------------------- */
function BrandingTab({
  tenantId,
  branding,
  setBranding,
  onSave,
  saving,
}: {
  tenantId: string;
  branding: TenantBrandingRow;
  setBranding: (b: TenantBrandingRow) => void;
  onSave: () => void;
  saving: boolean;
}) {
  const update = (patch: Partial<TenantBrandingRow>) => setBranding({ ...branding, ...patch });
  return (
    <div className="mt-5 grid gap-5 lg:grid-cols-[1fr_360px]">
      <div className="space-y-5">
        <Section title="Identidad">
          <Field label="Nombre de la marca">
            <input
              type="text"
              value={branding.brand_name}
              onChange={(e) => update({ brand_name: e.target.value })}
              className="input-styled"
            />
          </Field>
          <Field label="Tagline">
            <input
              type="text"
              value={branding.brand_tagline ?? ""}
              onChange={(e) => update({ brand_tagline: e.target.value || null })}
              className="input-styled"
              placeholder="ej: Producciones que vibran"
            />
          </Field>
        </Section>

        <Section title="Assets visuales">
          <div className="grid gap-4 sm:grid-cols-2">
            <AssetUploadField
              label="Logo principal"
              tenantId={tenantId}
              kind="logo"
              value={branding.logo_url}
              onChange={(url) => update({ logo_url: url })}
            />
            <AssetUploadField
              label="Logo dark"
              hint="Opcional · para fondos claros"
              tenantId={tenantId}
              kind="logo-dark"
              value={branding.logo_dark_url}
              onChange={(url) => update({ logo_dark_url: url })}
            />
            <AssetUploadField
              label="Favicon"
              tenantId={tenantId}
              kind="favicon"
              value={branding.favicon_url}
              onChange={(url) => update({ favicon_url: url })}
              preview="square"
            />
            <AssetUploadField
              label="Imagen hero"
              hint="Banner para checkout y tickets"
              tenantId={tenantId}
              kind="hero"
              value={branding.hero_image_url}
              onChange={(url) => update({ hero_image_url: url })}
              preview="wide"
            />
          </div>
        </Section>

        <Section title="Colores">
          <div className="grid gap-4 sm:grid-cols-3">
            <ColorField label="Primary" value={branding.primary_color} onChange={(v) => update({ primary_color: v })} />
            <ColorField label="Secondary" value={branding.secondary_color} onChange={(v) => update({ secondary_color: v })} />
            <ColorField label="Accent" value={branding.accent_color} onChange={(v) => update({ accent_color: v })} />
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">
            Usá formato OKLCH (ej: <code className="rounded bg-secondary px-1">oklch(0.65 0.28 295)</code>) o hex.
          </p>
        </Section>

        <Section title="Tipografía y forma">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Font heading">
              <input
                type="text"
                value={branding.font_family_heading ?? ""}
                onChange={(e) => update({ font_family_heading: e.target.value || null })}
                className="input-styled"
                placeholder="Inter, sans-serif"
              />
            </Field>
            <Field label="Font body">
              <input
                type="text"
                value={branding.font_family_body ?? ""}
                onChange={(e) => update({ font_family_body: e.target.value || null })}
                className="input-styled"
                placeholder="Inter"
              />
            </Field>
            <Field label="Border radius">
              <input
                type="text"
                value={branding.border_radius ?? ""}
                onChange={(e) => update({ border_radius: e.target.value || null })}
                className="input-styled"
                placeholder="0.75rem"
              />
            </Field>
          </div>
        </Section>

        <Section title="Atribución">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={branding.show_powered_by_zyntra}
              onChange={(e) => update({ show_powered_by_zyntra: e.target.checked })}
              className="h-4 w-4 rounded border-border bg-secondary"
            />
            <span className="text-sm text-foreground">Mostrar “Powered by ZYNTRA” en la experiencia</span>
          </label>
        </Section>

        <button
          onClick={onSave}
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-xl gradient-primary px-5 py-2.5 text-sm font-bold text-primary-foreground disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Guardar branding
        </button>
      </div>

      <div className="lg:sticky lg:top-24 lg:self-start">
        <BrandingPreview branding={branding} />
      </div>
    </div>
  );
}

/* ------------------------- Settings tab ------------------------- */
function SettingsTab({
  settings,
  setSettings,
  onSave,
  saving,
}: {
  settings: TenantSettingsRow;
  setSettings: (s: TenantSettingsRow) => void;
  onSave: () => void;
  saving: boolean;
}) {
  const update = (patch: Partial<TenantSettingsRow>) => setSettings({ ...settings, ...patch });
  return (
    <div className="mt-5 max-w-3xl space-y-5">
      <Section title="Localización">
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Locale">
            <input className="input-styled" value={settings.locale} onChange={(e) => update({ locale: e.target.value })} />
          </Field>
          <Field label="Moneda (ISO 4217)">
            <input
              className="input-styled uppercase"
              maxLength={3}
              value={settings.currency}
              onChange={(e) => update({ currency: e.target.value.toUpperCase() })}
            />
          </Field>
          <Field label="Timezone">
            <input className="input-styled" value={settings.timezone} onChange={(e) => update({ timezone: e.target.value })} />
          </Field>
        </div>
      </Section>

      <Section title="Contacto">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Email de soporte">
            <input
              type="email"
              className="input-styled"
              value={settings.support_email ?? ""}
              onChange={(e) => update({ support_email: e.target.value || null })}
            />
          </Field>
          <Field label="Teléfono">
            <input
              className="input-styled"
              value={settings.contact_phone ?? ""}
              onChange={(e) => update({ contact_phone: e.target.value || null })}
            />
          </Field>
        </div>
      </Section>

      <Section title="Legal y mensajes">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="URL Términos">
            <input className="input-styled" value={settings.terms_url ?? ""} onChange={(e) => update({ terms_url: e.target.value || null })} />
          </Field>
          <Field label="URL Privacidad">
            <input className="input-styled" value={settings.privacy_url ?? ""} onChange={(e) => update({ privacy_url: e.target.value || null })} />
          </Field>
        </div>
        <Field label="Mensaje en checkout">
          <textarea
            className="input-styled min-h-[60px]"
            value={settings.checkout_message ?? ""}
            onChange={(e) => update({ checkout_message: e.target.value || null })}
          />
        </Field>
        <Field label="Footer en tickets">
          <textarea
            className="input-styled min-h-[60px]"
            value={settings.tickets_footer_text ?? ""}
            onChange={(e) => update({ tickets_footer_text: e.target.value || null })}
          />
        </Field>
      </Section>

      <Section title="Funcionalidades">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={settings.emails_enabled}
            onChange={(e) => update({ emails_enabled: e.target.checked })}
            className="h-4 w-4 rounded border-border"
          />
          <span className="text-sm text-foreground">Enviar emails transaccionales</span>
        </label>
        <label className="mt-2 flex items-center gap-2">
          <input
            type="checkbox"
            checked={settings.resale_enabled_default}
            onChange={(e) => update({ resale_enabled_default: e.target.checked })}
            className="h-4 w-4 rounded border-border"
          />
          <span className="text-sm text-foreground">Reventa habilitada por defecto en eventos nuevos</span>
        </label>
      </Section>

      <button
        onClick={onSave}
        disabled={saving}
        className="inline-flex items-center gap-2 rounded-xl gradient-primary px-5 py-2.5 text-sm font-bold text-primary-foreground disabled:opacity-50"
      >
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
        Guardar configuración
      </button>
    </div>
  );
}

/* ------------------------- Members tab ------------------------- */
function MembersTab({
  tenantId,
  members,
  onChange,
}: {
  tenantId: string;
  members: MembershipRow[];
  onChange: () => void | Promise<void>;
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<{ user_id: string; email: string; display_name: string | null }[]>([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newRole, setNewRole] = useState<"owner" | "admin" | "member">("member");

  async function search(q: string) {
    setQuery(q);
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const data = await searchUsersForMembership(q.trim());
      setResults(data);
    } catch (e: any) {
      setError(e?.message ?? "Error al buscar usuarios");
    } finally {
      setSearching(false);
    }
  }

  async function addMember(userId: string) {
    setAdding(userId);
    setError(null);
    const { error } = await supabase.from("tenant_memberships").insert({
      tenant_id: tenantId,
      user_id: userId,
      role: newRole,
    });
    setAdding(null);
    if (error) {
      setError(error.message);
    } else {
      setQuery("");
      setResults([]);
      await onChange();
    }
  }

  async function changeRole(membershipId: string, role: string) {
    const { error } = await supabase.from("tenant_memberships").update({ role }).eq("id", membershipId);
    if (error) setError(error.message);
    else await onChange();
  }

  async function removeMember(membershipId: string) {
    if (!confirm("¿Quitar a este miembro del tenant?")) return;
    const { error } = await supabase.from("tenant_memberships").delete().eq("id", membershipId);
    if (error) setError(error.message);
    else await onChange();
  }

  return (
    <div className="mt-5 max-w-3xl space-y-5">
      <Section title="Agregar miembro">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs font-semibold text-foreground">Buscar por email o nombre</label>
            <input
              type="text"
              value={query}
              onChange={(e) => search(e.target.value)}
              placeholder="ej: maria@..."
              className="input-styled mt-1"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-foreground">Rol</label>
            <select
              value={newRole}
              onChange={(e) => setNewRole(e.target.value as any)}
              className="input-styled mt-1"
            >
              <option value="member">member</option>
              <option value="admin">admin</option>
              <option value="owner">owner</option>
            </select>
          </div>
        </div>

        {searching && <p className="mt-3 text-xs text-muted-foreground">Buscando…</p>}

        {results.length > 0 && (
          <ul className="mt-3 divide-y divide-border rounded-lg border border-border bg-surface">
            {results.map((u) => {
              const already = members.some((m) => m.user_id === u.user_id);
              return (
                <li key={u.user_id} className="flex items-center justify-between p-3">
                  <div>
                    <p className="text-sm font-semibold text-foreground">{u.display_name ?? u.email}</p>
                    <p className="text-xs text-muted-foreground">{u.email}</p>
                  </div>
                  <button
                    onClick={() => addMember(u.user_id)}
                    disabled={already || adding === u.user_id}
                    className="inline-flex items-center gap-1.5 rounded-md gradient-primary px-3 py-1.5 text-xs font-bold text-primary-foreground disabled:opacity-50"
                  >
                    {adding === u.user_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                    {already ? "Ya es miembro" : "Agregar"}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </Section>

      <Section title={`Miembros (${members.length})`}>
        {members.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aún no hay miembros en este tenant.</p>
        ) : (
          <ul className="divide-y divide-border rounded-lg border border-border bg-surface">
            {members.map((m) => (
              <li key={m.id} className="flex items-center justify-between p-3">
                <div>
                  <p className="text-sm font-semibold text-foreground">
                    {m.display_name ?? m.user_id.slice(0, 8)}
                  </p>
                  <p className="text-[11px] text-muted-foreground font-mono">{m.user_id}</p>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={m.role}
                    onChange={(e) => changeRole(m.id, e.target.value)}
                    className="rounded-md border border-border bg-secondary px-2 py-1 text-xs text-foreground"
                  >
                    <option value="member">member</option>
                    <option value="admin">admin</option>
                    <option value="owner">owner</option>
                  </select>
                  <button
                    onClick={() => removeMember(m.id)}
                    className="rounded-md p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                    title="Quitar miembro"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {error && (
        <p className="rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive">{error}</p>
      )}
    </div>
  );
}

/* ------------------------- Building blocks ------------------------- */
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <h3 className="text-sm font-bold text-foreground">{title}</h3>
      <div className="mt-3 space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-xs font-semibold text-foreground">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string | null;
  onChange: (v: string | null) => void;
}) {
  // Try to extract a hex preview if the value is hex; otherwise show OKLCH swatch via CSS.
  return (
    <div>
      <label className="text-xs font-semibold text-foreground">{label}</label>
      <div className="mt-1 flex items-center gap-2">
        <div
          className="h-9 w-9 shrink-0 rounded-md border border-border"
          style={{ background: value ?? "transparent" }}
        />
        <input
          type="text"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value || null)}
          placeholder="oklch(0.65 0.28 295) o #a855f7"
          className="input-styled"
        />
      </div>
    </div>
  );
}
